import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../services/authentication/authContext';

/**
 * One stable URL per feature for outbound communication (product update emails):
 * logged-out readers land on the static marketing page, logged-in customers go
 * straight to the feature inside the dashboard. The in-app target's own route
 * guard handles accounts without the feature (an invoice-only account is sent
 * back to /home), so this map never needs entitlement logic.
 */
const FEATURE_LINKS: Record<string, { publicPath: string; publicPathNl?: string; appPath: string }> = {
  'product-utilization': {
    publicPath: '/product/waste-tracking/',
    publicPathNl: '/producten/verspilling/',
    appPath: '/utilization',
  },
  'invoice-tool': {
    publicPath: '/product/invoice-processing/',
    publicPathNl: '/producten/factuurverwerking/',
    appPath: '/invoices',
  },
  // No marketing page for the tour — logged-out readers get the homepage.
  'guided-tour': {
    publicPath: '/',
    appPath: '/dashboard',
  },
  // Support lives inside the dashboard; there is no separate marketing page.
  support: {
    publicPath: '/',
    appPath: '/support',
  },
  inventory: {
    publicPath: '/product/inventory-management/',
    publicPathNl: '/producten/voorraadbeheer/',
    appPath: '/inventory',
  },
  'live-cost-percentage': {
    publicPath: '/',
    appPath: '/costprice',
  },
};

/** /features/:slug — the smart link used as CTA target in product update emails. */
export function FeatureLinkRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    const link = slug ? FEATURE_LINKS[slug] : undefined;

    if (user) {
      navigate(link?.appPath ?? '/dashboard', { replace: true });
      return;
    }
    // The marketing pages are static HTML outside the SPA — a full navigation.
    const wantsDutch = navigator.language?.toLowerCase().startsWith('nl');
    const target = link ? (wantsDutch && link.publicPathNl ? link.publicPathNl : link.publicPath) : '/';
    window.location.replace(target);
  }, [slug, user, loading, navigate]);

  return null;
}
